import { measurementMetrics, programTypes } from 'boot/globals';
import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import {
  chain, concat, forEach, get, includes, isArray, isBoolean, isDate, isEmpty, isFunction, isObject, join,
  map, mapValues, pick, reduce, replace, some,
} from 'lodash';
import Mustache from 'mustache';
import { mapActions, mapState } from 'pinia';
import { useFeatureFlags } from 'src/composables/analytics/useFeatureFlags';
import { PLANNED_MASTERY_ENABLED } from 'src/composables/analytics/usePlannedMasteryFeature';
import { CATEGORY_IDENTIFIERS } from 'src/constants/dynamicFields';
import { PLANNED_MASTERY_FIELD_IDS } from 'src/constants/fieldPresets/program';
import { FIELD_PREFIXES } from 'src/domains/reporting/constants';
import AssessmentTypeFieldGroupsDB from 'src/fb/assessmentTypeFieldGroups-db';
import AssessmentTypeFieldsDB from 'src/fb/assessmentTypeFields-db';
import Countries from 'src/mixins/Countries';
import Personalize from 'src/mixins/Personalize';
import Time from 'src/mixins/Time';
import Toolbox from 'src/mixins/Toolbox';
import { assessments } from 'src/store/assessments';
import { dataFields } from 'src/store/dataFields';
import { treatmentPlans } from 'src/store/treatmentPlans';
import { workflows as workflowsStore } from 'src/store/workflows';
import { calculateAge } from 'src/utils/DateUtils';
import { fieldCalculators } from 'src/utils/FieldUtils';
import { logEvent } from 'src/utils/LogEvent';
import { getPlannedMasteryDate } from 'src/utils/PlannedMasteryUtils';
import { resolveCredentialOn } from 'src/contexts/staffCompetency';

/**
 * The credential these tokens print for a person.
 *
 * VALID TODAY, not merely marked active. A body can hold several records, because a renewal is a new
 * one carrying its own window, so filtering on the stored standing alone returned the OLDEST and
 * these tokens printed an expired certificate number onto a signed note.
 *
 * The rule is published by Staff Competency and shared with the staff-list columns, which is the
 * point: the two had it written separately and gave different answers for one record.
 *
 * TODAY, NOT THE SESSION DATE. These tokens receive only the staff member, so there is no date to
 * resolve against — the same limitation the stored-standing filter had.
 */
const printableCredential = (staffMember, today) => {
  return resolveCredentialOn(staffMember?.credentials ?? [], today);
};

export default {
  mixins: [Countries, Toolbox, Personalize, Time],

  data() {
    return {
      headingStyles: {
        title: {
          value: 'title',
          label: this.$t('templates.headingStyles.title'),
          classes: 'q-pl-none text-title',
          renderClass: 'report-title',
        },
        h1: {
          value: 'h1',
          label: this.$t('templates.headingStyles.h1'),
          classes: 'q-pl-none text-bold',
          renderClass: 'report-h1',
        },
        h2: {
          value: 'h2',
          label: this.$t('templates.headingStyles.h2'),
          classes: 'q-pl-xs',
          renderClass: 'report-h2',
        },
        h3: {
          value: 'h3',
          label: this.$t('templates.headingStyles.h3'),
          classes: 'q-pl-sm',
          renderClass: 'report-h3',
        },
        h4: {
          value: 'h4',
          label: this.$t('templates.headingStyles.h4'),
          classes: 'q-pl-md',
          renderClass: 'report-h4',
        },
      },
    };
  },

  computed: {
    ...mapState(treatmentPlans, ['clinicTreatmentAreas']),
    ...mapState(workflowsStore, ['workflows']),
    ...mapState(assessments, ['assessmentTypes']),
    ...mapState(dataFields, ['clientDataFields', 'allStaffMemberDataFields', 'visibleProgramDataFields']),

    // Read through isFeatureEnabled rather than the feature composable: a mixin has no setup(), and
    // calling the composable in a getter would build a fresh computed on every evaluation.
    plannedMasteryEnabled() {
      return useFeatureFlags().isFeatureEnabled(PLANNED_MASTERY_ENABLED);
    }, // plannedMasteryEnabled()

    clientDataFieldPresets() {
      const fixedClientPresets = [
        {
          token: 'client.clientName',
          id: 'clientName',
          label: this.$t('templates.client.clientName'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.firstName',
          id: 'firstName',
          label: this.$t('templates.client.firstName'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.lastName',
          id: 'lastName',
          label: this.$t('templates.client.lastName'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.alias',
          id: 'alias',
          label: this.$t('templates.client.alias'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          canEdit: false,
        },
        {
          token: 'client.birthDate',
          id: 'birthDate',
          label: this.$t('templates.client.birthDate'),
          hint: null,
          category: 'client',
          type: 'date',
          allowMultiLine: false,
          prompt: false,
          canEdit: false,
        },
        {
          token: 'client.age',
          id: 'age',
          label: this.$t('templates.client.age'),
          hint: null,
          category: 'client',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          canEdit: false,
          calculate: (client) => calculateAge(get(client, 'birthDate')),
        },
        {
          token: 'client.reference',
          id: 'reference',
          label: this.$t('templates.client.reference'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.address1',
          id: 'address1',
          label: this.$t('templates.client.address1'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.address2',
          id: 'address2',
          label: this.$t('templates.client.address2'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.postalCode',
          id: 'postalCode',
          label: this.$t('templates.client.postalCode'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.city',
          id: 'city',
          label: this.$t('templates.client.city'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.state',
          id: 'state',
          label: this.$t('templates.client.state'),
          hint: null,
          category: 'client',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (client) => chain(this.countryStates(client.country))
            .find({ code: client.state })
            .get('label', '')
            .defaultTo(client.state || '')
            .value(),
        },

        {
          token: 'client.country',
          id: 'country',
          label: this.$t('templates.client.country'),
          hint: null,
          category: 'client',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (client) => (client.country ? this.$t(client.country) : ''),
        },
        {
          token: 'client.phone',
          id: 'phone',
          label: this.$t('templates.client.phone'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.email',
          id: 'email',
          label: this.$t('templates.client.email'),
          hint: null,
          category: 'client',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'client.gender',
          id: 'gender',
          label: this.$t('templates.client.gender'),
          hint: null,
          category: 'client',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (client) => (get(this.$tm('clients.modals.editClient.gender'), client.gender, '')),
        },
      ];
      return concat(fixedClientPresets, map(this.clientDataFields, (dataField) => ({
        token: `client.${FIELD_PREFIXES.DATAFIELD}${dataField.id}`,
        id: `${FIELD_PREFIXES.DATAFIELD}${dataField.id}`,
        label: dataField.label,
        hint: null,
        category: 'client',
        type: dataField.type,
        allowMultiLine: dataField.allowMultiLine,
        prompt: false,
      })));
    }, // clientDataFieldPresets()

    staffMemberDataFieldPresets() {
      const fixedStaffMemberPresets = [
        {
          token: 'staffMember.clientName',
          id: 'clientName',
          label: this.$t('templates.staffMember.clientName'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.firstName',
          id: 'firstName',
          label: this.$t('templates.staffMember.firstName'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.lastName',
          id: 'lastName',
          label: this.$t('templates.staffMember.lastName'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.birthDate',
          id: 'birthDate',
          label: this.$t('templates.staffMember.birthDate'),
          hint: null,
          category: 'staffMember',
          type: 'date',
          allowMultiLine: false,
          prompt: false,
          canEdit: false,
        },
        {
          token: 'staffMember.age',
          id: 'age',
          label: this.$t('templates.staffMember.age'),
          hint: null,
          category: 'staffMember',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          canEdit: false,
          calculate: (client) => calculateAge(get(client, 'birthDate')),
        },
        {
          token: 'staffMember.reference',
          id: 'reference',
          label: this.$t('templates.staffMember.reference'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.address1',
          id: 'address1',
          label: this.$t('templates.staffMember.address1'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.address2',
          id: 'address2',
          label: this.$t('templates.staffMember.address2'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.postalCode',
          id: 'postalCode',
          label: this.$t('templates.staffMember.postalCode'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.city',
          id: 'city',
          label: this.$t('templates.staffMember.city'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.state',
          id: 'state',
          label: this.$t('templates.staffMember.state'),
          hint: null,
          category: 'staffMember',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (client) => chain(this.countryStates(client?.country))
            .find({ code: client?.state })
            .get('label', '')
            .defaultTo(client?.state || '')
            .value(),
        },

        {
          token: 'staffMember.country',
          id: 'country',
          label: this.$t('templates.staffMember.country'),
          hint: null,
          category: 'staffMember',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (client) => (client?.country ? this.$t(client.country) : ''),
        },
        {
          token: 'staffMember.phone',
          id: 'phone',
          label: this.$t('templates.staffMember.phone'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.email',
          id: 'email',
          label: this.$t('templates.staffMember.email'),
          hint: null,
          category: 'staffMember',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'staffMember.gender',
          id: 'gender',
          label: this.$t('templates.staffMember.gender'),
          hint: null,
          category: 'staffMember',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (client) => (get(this.$tm('clients.modals.editClient.gender'), client?.gender, '')),
        },
        {
          token: 'staffMember.level',
          id: 'level',
          label: this.$t('templates.staffMember.level'),
          hint: null,
          category: 'staffMember',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (staffMember) => (printableCredential(staffMember, this.todayCalendarDate())?.level || ''),
        },
        {
          token: 'staffMember.certificationNumber',
          id: 'certificationNumber',
          label: this.$t('templates.staffMember.certificationNumber'),
          hint: null,
          category: 'staffMember',
          type: 'calculated',
          allowMultiLine: false,
          prompt: false,
          calculate: (staffMember) => (printableCredential(staffMember, this.todayCalendarDate())?.certificateId || ''),
        },
      ];
      return concat(fixedStaffMemberPresets, map(this.allStaffMemberDataFields, (dataField) => ({
        token: `staffMember.${FIELD_PREFIXES.DATAFIELD}${dataField.id}`,
        id: `${FIELD_PREFIXES.DATAFIELD}${dataField.id}`,
        label: dataField.label,
        hint: null,
        category: 'staffMember',
        type: dataField.type,
        allowMultiLine: dataField.allowMultiLine,
        prompt: false,
      })));
    }, // staffMemberDataFieldPresets()

    reportDataFieldPresets() {
      return [
        {
          category: 'report',
          token: 'report.currentPage',
          label: this.$t('templates.report.currentPage'),
          canEdit: false,
          id: 'currentPage',
        },
        {
          category: 'report',
          token: 'report.pageCount',
          label: this.$t('templates.report.pageCount'),
          canEdit: false,
          id: 'pageCount',
        },
      ];
    },

    programDataFieldsPreset() {
      const fixedProgramPresets = [
        {
          category: 'program',
          token: 'program.programName',
          label: this.$t('templates.program.programName'),
          canEdit: false,
          id: 'programName',
        },
        {
          category: 'program',
          token: 'program.programIndex',
          label: this.$t('templates.program.programIndex'),
          canEdit: false,
          id: 'programIndex',
        },
        {
          category: 'program',
          token: 'program.primaryMetric',
          label: this.$t('templates.program.measurementMetric'),
          canEdit: false,
          id: 'primaryMetric',
          type: 'calculated',
          calculate: (program) => {
            const metricId = program?.primaryMetric || programTypes[program?.type]?.defaultMetric;
            return measurementMetrics.find((metric) => metric.id === metricId)?.label ?? '';
          },
        },
        {
          category: 'program',
          token: 'program.workflow',
          label: this.$t('insights.chart.masteryCriteria'),
          canEdit: false,
          id: 'workflow',
          type: 'calculated',
          calculate: (program) => {
            if (!program?.workflow) {
              return this.$t('common.notSelected');
            }
            return this.workflows.find(({ id }) => id === program.workflow)?.workflowName || '';
          },
        },
        {
          category: 'program',
          token: 'program.completionPercentage',
          label: this.$t('progress.columnsCompletionPercentageLabel'),
          canEdit: false,
          id: 'completionPercentage',
          type: 'calculated',
          calculate: (program) => fieldCalculators.programCompletionPercentage({ program, i18n }),
        },
        {
          category: 'program',
          token: 'program.treatmentArea',
          label: this.$t('templates.program.treatmentArea'),
          canEdit: false,
          id: 'treatmentArea',
          type: 'calculated',
          calculate: (program) => (chain(this.clinicTreatmentAreas)
            .find({ id: get(program, 'treatmentArea') })
            .get('label')
            .value())
          ,
        },
        {
          category: 'program',
          token: 'program.treatmentAreaCategoryLabel',
          label: this.$t('templates.program.treatmentAreaCategoryLabel'),
          canEdit: false,
          id: 'treatmentAreaCategoryLabel',
          type: 'calculated',
          calculate: (program) => (chain(this.clinicTreatmentAreas)
            .find({ id: get(program, 'treatmentArea') })
            .get('subTreatmentAreasLabel')
            .value())
          ,
        },
        {
          category: 'program',
          token: 'program.subTreatmentArea',
          label: this.$t('templates.program.subTreatmentArea'),
          canEdit: false,
          id: 'subTreatmentArea',
          type: 'calculated',
          calculate: (program) => (chain(this.clinicTreatmentAreas)
            .find({ id: get(program, 'subTreatmentArea') })
            .get('label')
            .value())
          ,
        },
        {
          category: 'program',
          token: 'program.baseline',
          label: this.$t('templates.program.baseline'),
          canEdit: false,
          id: 'baseline',
          type: 'calculated',
          calculate: (program) => (this.formatProgramField(program.baseline)),
        },
        {
          category: 'program',
          token: 'program.objective',
          label: this.$t('templates.program.objective'),
          canEdit: false,
          id: 'objective',
          type: 'calculated',
          calculate: (program) => (this.formatProgramField(program.objective)),
        },
        {
          category: 'program',
          token: 'program.statusChange_baseline',
          label: this.$t('templates.program.statusChange_baseline'),
          canEdit: false,
          id: 'statusChange_baseline',
          type: 'date',
        },
        {
          category: 'program',
          token: 'program.statusChange_active',
          label: this.$t('templates.program.statusChange_active'),
          canEdit: false,
          id: 'statusChange_active',
          type: 'date',
        },
        {
          category: 'program',
          token: 'program.statusChange_closed',
          label: this.$t('templates.program.statusChange_closed'),
          canEdit: false,
          id: 'statusChange_closed',
          type: 'date',
        },
        {
          category: 'program',
          token: 'program.statusChange_maintenance',
          label: this.$t('templates.program.statusChange_maintenance'),
          canEdit: false,
          id: 'statusChange_maintenance',
          type: 'date',
        },
        {
          category: 'program',
          token: 'program.statusChange_hold',
          label: this.$t('templates.program.statusChange_hold'),
          canEdit: false,
          id: 'statusChange_hold',
          type: 'date',
        },
        {
          category: 'program',
          token: 'program.statusChange_discontinued',
          label: this.$t('templates.program.statusChange_discontinued'),
          canEdit: false,
          id: 'statusChange_discontinued',
          type: 'date',
        },
        {
          category: 'program',
          token: 'program.plannedMasteryTimeframe',
          label: this.$t('templates.program.plannedMasteryTimeframe'),
          canEdit: false,
          id: 'plannedMasteryTimeframe',
          type: 'calculated',
          calculate: (program) => {
            const tf = get(program, 'plannedMasteryTimeframe');
            if (!tf?.quantity || !tf?.unit) return '';
            return this.$tc(`editProgram.masteryTimeFrames.${tf.unit}`, tf.quantity, { count: tf.quantity });
          },
        },
        {
          category: 'program',
          token: 'program.plannedMasteryDate',
          label: this.$t('templates.program.plannedMasteryDate'),
          canEdit: false,
          id: 'plannedMasteryDate',
          type: 'calculated',
          calculate: (program) => {
            const masteryDate = getPlannedMasteryDate(program, this.addToDate);
            return masteryDate instanceof Timestamp ? this.getLocaleDateString(masteryDate) : '';
          },
        },
      ];
      const visiblePresets = this.plannedMasteryEnabled
        ? fixedProgramPresets
        : fixedProgramPresets.filter(({ id }) => !PLANNED_MASTERY_FIELD_IDS.includes(id));

      return concat(visiblePresets, map(this.visibleProgramDataFields, (programDataField) => ({
        token: `program.${FIELD_PREFIXES.DATAFIELD}${programDataField.id}`,
        id: `${FIELD_PREFIXES.DATAFIELD}${programDataField.id}`,
        label: programDataField.label,
        hint: null,
        category: 'program',
        type: programDataField.type,
        allowMultiLine: programDataField.allowMultiLine,
        prompt: false,
      })));
    }, // programDataFieldsPreset()

    clientFieldCategory() {
      return {
        categoryIdentifier: CATEGORY_IDENTIFIERS.CLIENT,
        categoryLabel: this.$t('templates.categories.client'),
        fields: this.clientDataFieldPresets,
      };
    }, // clientFieldCategory()

    staffMemberFieldCategory() {
      return {
        categoryIdentifier: CATEGORY_IDENTIFIERS.STAFF_MEMBER,
        categoryLabel: this.$t('templates.categories.staffMember'),
        fields: this.staffMemberDataFieldPresets,
      };
    }, // staffMemberFieldCategory()

    reportFieldCategory() {
      return {
        categoryIdentifier: CATEGORY_IDENTIFIERS.REPORT,
        categoryLabel: this.$t('templates.categories.report'),
        fields: this.reportDataFieldPresets,
      };
    },

    programFieldCategory() {
      return {
        categoryIdentifier: CATEGORY_IDENTIFIERS.PROGRAM,
        categoryLabel: this.$t('templates.categories.program'),
        fields: this.programDataFieldsPreset,
      };
    }, // programFieldCategory()

    assessmentTypeFieldCategory() {
      const fields = [];

      if (Object.keys(this.assessmentTypeCategories).length) {
        Object.keys(this.assessmentTypeCategories).forEach((category) => {
          fields.push(...this.assessmentTypeCategories[category].fields);
          return category;
        });
      }

      return {
        categoryIdentifier: CATEGORY_IDENTIFIERS.ASSESSMENT_TYPE,
        categoryLabel: this.$t('templates.categories.assessmentType'),
        fields,
      };
    }, // assessmentTypeFieldCategory()

    textContentFieldCategories() {
      return [
        this.clientFieldCategory,
      ];
    }, // textContentFieldCategories()

    reportElementFieldCategories() {
      return [
        this.reportFieldCategory,
        this.clientFieldCategory,
      ];
    }, // reportElementFieldCategories()

    programGroupFieldCategories() {
      return [this.clientFieldCategory, this.programFieldCategory];
    }, // programGroupFieldCategories()

    supervisionFieldPresets() {
      return [{
        category: null,
        token: 'programMods',
        tokenType: 'dynamicField',
        label: this.$t('settings.supervision.fieldProgramMods'),
        canEdit: false,
        type: 'text',
        id: 'programMods',
      }];
    }, // supervisionFieldPresets()

    supervisionFieldCategories() {
      return [
        {
          categoryIdentifier: null,
          categoryLabel: null,
          fields: this.supervisionFieldPresets,
        },
      ];
    }, // supervisionFieldCategories()

    headingStylesOptions() {
      return map(this.headingStyles, (headingStyle) => pick(headingStyle, ['value', 'label']));
    },

  },

  methods: {
    ...mapActions(assessments, ['loadAssessmentTypes']),

    getTokenHtml(tokenField, tokenLabel, tokenType = 'dynamicField') {
      const contentClass = (tokenType === 'dataSection') ? 'data-section' : 'token';

      return `<span data-type="${tokenType}" data-field="${
        tokenField}" data-label="${tokenLabel}" class="${contentClass}" contenteditable="false" tabindex="-1"></span>`;
    }, // getTokenHtml()

    convertTokensToHtml(tokenFields, editTemplateText) {
      if (isEmpty(tokenFields)) {
        return editTemplateText;
      }
      const data = chain(tokenFields)
        .keyBy('token')
        .mapValues((field) => this.getTokenHtml(field.token, field.label, field.tokenType))
        .value();
      return this.replaceTags(editTemplateText, data);
    }, // convertTokensToHtml()

    convertHtmlToTokens(tokenFields, editTemplateText) {
      const data = chain(tokenFields)
        .keyBy('token')
        .mapValues((field) => `{{${field.token}}}`)
        .value();
      return this.replaceHtml(editTemplateText, data);
    }, // convertHtmlToTokens()

    getFormattedDataField(source, dataField) {
      let dataFieldId = dataField.id;
      if (includes(dataField.id, FIELD_PREFIXES.DATAFIELD)) {
        dataFieldId = replace(dataField.id, FIELD_PREFIXES.DATAFIELD, '');
      }
      if (includes(dataField.id, FIELD_PREFIXES.STATUS_CHANGE)) {
        dataFieldId = replace(dataField.id, FIELD_PREFIXES.STATUS_CHANGE, '');
      }

      const data = get(source, dataFieldId);
      switch (dataField.type) {
        case 'date': {
          if (data instanceof Timestamp) {
            return this.getLocaleDateString(data);
          }
          return '';
        }
        case 'select': {
          if (isArray(data)) {
            return join(data, ', ');
          }
          break;
        }
        case 'yesNo': {
          if (data === null || data === undefined) {
            return this.$t('common.notSelected');
          }
          return data ? this.$t('common.buttonYes') : this.$t('common.buttonNo');
        }
        case 'user': {
          return data?.userName || '';
        }
        case 'imageFile': {
          // Must be references in fieldTransformations since it's expected to be raw HTML
          return null;
        }
        case 'calculated': {
          if (!chain(dataField).get('calculate').isFunction().value()) {
            return '';
          }
          return dataField.calculate(source);
        }
        default:
      }
      return data;
    }, // getFormattedDataField()

    getFormattedAssessmentTypeDataFields(assessmentTypeCategory, assessmentData) {
      return reduce(
        assessmentTypeCategory.fields,
        (acc, dataField) => {
          if (get(dataField, 'id').startsWith(FIELD_PREFIXES.DATAFIELD)) {
            acc[dataField.id] = this.getFormattedDataField(get(assessmentData, 'data'), dataField);
            return acc;
          }
          acc[dataField.id] = this.getFormattedDataField(assessmentData, dataField);
          return acc;
        },
        {},
      );
    }, // getFormattedAssessmentTypeDataFields()

    getFormattedProgramDataFields(program) {
      return {
        ...chain(this.programDataFieldsPreset)
          .reduce((acc, dataField) => {
            if (get(dataField, 'id').startsWith(FIELD_PREFIXES.DATAFIELD)) {
              acc[dataField.id] = this.getFormattedDataField(get(program, 'customData'), dataField);
              return acc;
            }
            if (get(dataField, 'id').startsWith(FIELD_PREFIXES.STATUS_CHANGE)) {
              acc[dataField.id] = this.getFormattedDataField(get(program, 'statusChanges'), dataField);
              return acc;
            }
            acc[dataField.id] = this.getFormattedDataField(program, dataField);
            return acc;
          }, {})
          .value(),
      };
    }, // getFormattedProgramDataFields()

    getFormattedClientDataFields(client) {
      return {
        ...chain(this.clientDataFieldPresets)
          .reduce((acc, dataField) => {
            if (get(dataField, 'id').startsWith(FIELD_PREFIXES.DATAFIELD)) {
              acc[dataField.id] = this.getFormattedDataField(get(client, 'customData'), dataField);
              return acc;
            }
            acc[dataField.id] = this.getFormattedDataField(client, dataField);
            return acc;
          }, {})
          .value(),
      };
    }, // getFormattedClientDataFields()

    getFormattedStaffMemberDataFields(staffMember) {
      return {
        ...chain(this.staffMemberDataFieldPresets)
          .reduce((acc, dataField) => {
            if (get(dataField, 'id').includes(FIELD_PREFIXES.DATAFIELD)) {
              acc[dataField.id] = this.getFormattedDataField(get(staffMember, 'customData'), dataField);
              return acc;
            }
            acc[dataField.id] = this.getFormattedDataField(staffMember, dataField);
            return acc;
          }, {})
          .value(),
      };
    }, // getFormattedStaffMemberDataFields()

    replaceTags(string, data = {}) {
      const plainTextString = replace(string, '\n', '<br/>');
      return Object
        .entries(data)
        .reduce(
          (res, [key, value]) => res.replace(new RegExp(`{{${key}}}`, 'g'), value),
          plainTextString,
        );
    }, // replaceTags()

    replaceHtml(string, data = {}) {
      /* eslint-disable max-len */
      return Object
        .entries(data)
        .reduce(
          (res, [key, value]) => res
            .replace(
              new RegExp(`<span (data-type="(dynamicField|dataSection)")? data-field="${key}" data-label="([^"]*)" class="([^"]*)"[^>]*></span>`, 'g'),
              value,
            ),
          string,
        );
      /* eslint-enable max-len */
    }, // replaceHtml()

    getTokensFromHtml(string) {
      // eslint-disable-next-line max-len
      const tokenRegEx = /<span (data-type="(dynamicField|dataSection)")? data-field="([^"]*)" data-label="([^"]*)" class="([^"]*)"[^>]*><\/span>/g;

      return chain([...string.matchAll(tokenRegEx)])
        .map((tokenMatch) => (get(tokenMatch, 3)))
        .compact()
        .value();
    }, // replaceHtml()

    /**
     * Replace base64-pdf img with alternative text showing warning
     *
     * @param {string} templateText
     * @return {string}
     */
    replacePdfBase64(templateText) {
      return templateText
        .replace(
          /<img src=['"]data:application\/pdf;base64,(.+?)['"].*?>/g,
          (match, pdfBase64) => {
            if (pdfBase64) {
              this.notifyWarning(
                this.$t('progressReport.warnPdfBase64InsideTemplate'),
                {
                  timeout: 8000,
                  badgeClass: 'hidden',
                },
              );
            }

            return `<p>${this.$t('progressReport.notSupportedImageFormat')}</p>`;
          },
        );
    }, // replacePdfBase64()

    formatProgramField(fieldText) {
      // return replace(
      return this.personalize(fieldText);// , /(\r\n|\n\r|\r|\n)/g, '<br/>');
    },

    /**
     * @param {string} templateText
     * @returns {string[]}
     */
    fetchTemplateFields(templateText) {
      let rawTemplateInfo;

      try {
        rawTemplateInfo = Mustache.parse(templateText);
      } catch (e) {
        rawTemplateInfo = templateText;
        this.notifyNegative(this.$t('settings.reportTemplates.edit.templateHasMustacheError'));
      }

      return chain(rawTemplateInfo)
        .filter((templatePart) => (get(templatePart, 0) === 'name'))
        .map((templatePart) => get(templatePart, 1))
        .uniq()
        .value();
    }, // fetchTemplateFields()

    formatTemplateField(fieldData, fieldTransformation = null) {
      if (fieldTransformation !== null) {
        if (isFunction(fieldTransformation)) {
          return fieldTransformation(fieldData);
        }
      }
      if (isBoolean(fieldData)) {
        return fieldData ? this.$t('common.buttonYes') : this.$t('common.buttonNo');
      }
      if (isDate(fieldData)) {
        return this.getLocaleDateString(fieldData);
      }
      if (isArray(fieldData) && (fieldData.length > 1) && (fieldTransformation === null)) {
        return map(fieldData, (value, index) => (index === 0 ? value : ` ${value}`));
      }
      return fieldData;
    },

    renderTemplate(templateText, fields = {}, fieldTransformations = {}) {
      let preparedTemplateText = templateText;
      forEach(fieldTransformations, (dest, src) => {
        if (!isFunction(dest)) {
          preparedTemplateText = replace(preparedTemplateText, new RegExp(`{{${src}}}`, 'g'), dest);
        }
      });
      const fieldsFormatted = mapValues(fields, (fieldCategory, fieldCategoryId) => {
        if (!isObject(fieldCategory) || isArray(fieldCategory) || isDate(fieldCategory)) {
          return this.formatTemplateField(
            fieldCategory,
            get(fieldTransformations, fieldCategoryId, null),
          );
        }

        return mapValues(fieldCategory, (fieldData, fieldId) => {
          return (
            this.formatTemplateField(fieldData, get(fieldTransformations, `${fieldCategoryId}.${fieldId}`, null))
          );
        });
      });

      let result;

      try {
        result = Mustache.render(preparedTemplateText, fieldsFormatted);
      } catch (e) {
        result = preparedTemplateText;
        // using notification directly causes an infinite loop
        // as per https://github.com/quasarframework/quasar/issues/15184 wrapped it into a timeout to avoid side effects
        setTimeout(() => {
          this.notifyNegative(this.$t('settings.reportTemplates.edit.templateHasMustacheError'));
        });
      }
      return result;
    }, // renderTemplate()

    templateFieldsHasGroup(templateFields, group) {
      return some(templateFields, (field) => (field?.startsWith(`${group}.`)));
    }, // templateFieldsHasGroup()

    getDefaultAssessmentTypeFields(assessmentTypeId) {
      return [
        {
          allowMultiLine: false,
          category: `assessmentType_${assessmentTypeId}`,
          hint: null,
          id: 'dateCompleted',
          label: this.$t('assessments.modals.editAssessment.labelDateCompleted'),
          caption: null,
          prompt: false,
          token: `assessmentType_${assessmentTypeId}.dateCompleted`,
          type: 'date',
        },
        {
          allowMultiLine: false,
          category: `assessmentType_${assessmentTypeId}`,
          hint: null,
          id: 'administratedBy',
          label: this.$t('assessments.modals.editAssessment.labelAdministeredBy'),
          caption: null,
          prompt: false,
          token: `assessmentType_${assessmentTypeId}.administratedBy`,
          type: 'user',
        },
        {
          allowMultiLine: false,
          category: `assessmentType_${assessmentTypeId}`,
          hint: null,
          id: 'informant',
          label: this.$t('assessments.modals.editAssessment.labelInformant'),
          caption: null,
          prompt: false,
          token: `assessmentType_${assessmentTypeId}.informant`,
          type: 'text',

          isInformantRequired: true,
        },
      ];
    }, // getDefaultAssessmentTypeFields()

    async getAssessmentTypeFields(assessmentTypeId) {
      this.$q.loading.show();
      try {
        const assessmentTypeFieldsDb = new AssessmentTypeFieldsDB();
        const assessmentTypeFieldGroupsDb = new AssessmentTypeFieldGroupsDB();
        const [assessmentTypeFields, assessmentTypeFieldGroups] = await Promise.all([
          assessmentTypeFieldsDb.readAll([['assessmentType', '==', assessmentTypeId]]),
          assessmentTypeFieldGroupsDb.readAll([['assessmentType', '==', assessmentTypeId]]),
        ]);

        return [
          ...this.getDefaultAssessmentTypeFields(assessmentTypeId),
          ...assessmentTypeFields
            .map((field) => {
              const group = assessmentTypeFieldGroups
                .find(({ id }) => id === field.assessmentTypeFieldGroup);

              const type = field.fieldType === 'imageFile' ? 'imageFile' : 'text';
              const tokenType = field.fieldType === 'imageFile' ? 'dataSection' : 'dynamicField';
              return {
                allowMultiLine: false,
                category: `assessmentType_${assessmentTypeId}`,
                hint: null,
                id: `${FIELD_PREFIXES.DATAFIELD}${field.id}`,
                label: field.label,
                caption: group.title || null,
                prompt: false,
                token: `assessmentType_${assessmentTypeId}.${FIELD_PREFIXES.DATAFIELD}${field.id}`,
                tokenType,
                type,

                order: ((group?.order) ?? 0) * 1000 + field.order,
              };
            })
            .sort((a, b) => a.order - b.order),
        ];
      } catch (error) {
        logEvent(error);
        console.error(`Error fetching assessment type fields: ${error}`);
        return [];
      } finally {
        this.$q.loading.hide();
      }
    }, // getAssessmentTypeFields()

    async getAssessmentTypeCategory(assessmentTypeId) {
      this.$q.loading.show();
      try {
        if (this.assessmentTypes.length === 0) {
          await this.loadAssessmentTypes();
        }

        const assessmentType = this.assessmentTypes
          .find((at) => at.id === assessmentTypeId);
        if (!assessmentType) {
          console.warn(`Assessment type with ID ${assessmentTypeId} not found.`);
          return {};
        }

        const fields = await this.getAssessmentTypeFields(assessmentTypeId);
        return {
          categoryIdentifier: `assessmentType_${assessmentTypeId}`,
          categoryLabel: assessmentType.assessmentTypeName,
          fields: fields.filter((field) => {
            return assessmentType.isInformantRequired
              || assessmentType.isInformantVisible
              || !field.isInformantRequired;
          }),
        };
      } catch (error) {
        logEvent(error);
        console.error(`Error fetching assessment type category: ${error}`);

        return {
          categoryIdentifier: `assessmentType_${assessmentTypeId}`,
          categoryLabel: null,
          fields: [],
        };
      } finally {
        this.$q.loading.hide();
      }
    }, // getAssessmentTypeCategory()

  },
};
